import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  MenuItem,
  Select,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Stack,
  Fab,
  CardContent,
  CardMedia,
  Card,
  IconButton,
  Container,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import background from "./Assets/background4.webp";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { DESKS } from "./helpers/Constants";
import { set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import UpIcon from "@mui/icons-material/KeyboardArrowUp";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { lazy, Suspense } from "react";
import { vehicleCodeChanger } from "./helpers/vehicleCodeChanger";
import PropTypes from "prop-types";
import ScrollFab from "./ScrollFab";
import carImage from "./Assets/peugeot-308.png"; // Importe a imagem PNG

const theme = createTheme({
  palette: {
    primary: {
      main: "#1C2541",
    },
    secondary: {
      main: "#EE8013",
    },
    terceary: {
      main: "#F4F4F4",
    },
  },
});

const Body = ({ choosedCar, setChoosedCar, showNavBar }) => {
  const [isLoading, setIsloading] = useState(false);
  const [getAvaiabilities, setAvaiabilities] = useState("");
  const [pickUpDesk, setPickUpDesk] = useState(41);
  const [dropOffDesk, setDropOffDesk] = useState(41);
  const [northDeskOutOfHours, setNorthDeskOutOfHours] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const { t } = useTranslation();
  const [openErrorModal, setOpenErrorModal] = useState(false); // State to control modal visibility
  const [openInformationModal, setOpenInformationModal] = useState(false); // State to control modal visibility
  const validateDateReturn = (value, { parent }) => {
    if (!value || !parent.date_pickup) {
      return true;
    }
    return value >= parent.date_pickup;
  };

  const ErrorModal = lazy(() => import("./Components/Modal/ErrorModal"));
  const InformationModal = lazy(
    () => import("./Components/Modal/InformationModal"),
  );

  const reservationTimeValidation = useMemo(() => {
    return yup.object({
      loc_pickup: yup.number().required(t("mandatory")),
      date_pickup: yup
        .date()
        .required(t("mandatory"))
        .min(dayjs().add(2, "day"), t("pickUpDateValidation"))
        .typeError(t("validDate"))
        .test("date_pickup", t("datesCanTBeEqual"), function (value) {
          const { time_pickup, time_return, date_pickup, date_return } =
            this.parent;

          if (
            date_pickup.getTime() === date_return.getTime() &&
            time_pickup === time_return
          ) {
            return false;
          } else {
            return true;
          }
        }),
      time_pickup: yup
        .string()
        .required(t("mandatory"))
        .test("time_pickup", t("outsideBusinessHours"), function (value) {
          const currentDesk = this?.parent?.loc_pickup ?? 41;
          const minMinutes = currentDesk === 46 ? 555 : 510; // 555 = 9*60 + 15
          const hours = parseInt(value.split(":")[0], 10);
          const minutes = parseInt(value.split(":")[1], 10);
          const totalMinutes = hours * 60 + minutes;

          if (currentDesk === 46) {
            if (hours < 9 || (hours === 9 && minutes < 15)) {
              setNorthDeskOutOfHours(true);
            } else {
              setNorthDeskOutOfHours(false);
            }
          } else {
            setNorthDeskOutOfHours(false);
          }

          if (totalMinutes < minMinutes || totalMinutes > 1110) {
            // Return different messages based on current desk
            return this.createError({
              message:
                currentDesk === 46
                  ? t("northOutsideBusinessHours")
                  : t("outsideBusinessHours"),
            });
          }

          return totalMinutes >= minMinutes && totalMinutes <= 1110;
        })
        .test(
          "valid_time_pickup",
          t("timePickUpAfterReturn"),
          function (value) {
            const { time_pickup, time_return, date_pickup, date_return } =
              this.parent;

            if (!value || !time_pickup || !date_pickup || !date_return) {
              return true;
            }

            if (
              date_pickup.getTime() === date_return.getTime() &&
              time_pickup > time_return
            ) {
              return false;
            }

            return true;
          },
        ),
      loc_return: yup.number().required(t("mandatory")),
      date_return: yup
        .date()
        .required(t("mandatory"))
        .typeError(t("validDate"))
        .test("dateReturn", t("returnDateValidation"), validateDateReturn),
      time_return: yup
        .string()
        .required(t("mandatory"))
        .test("time_return", t("northOutsideBusinessHours"), function (value) {
          if (!value) return false;

          const currentDesk = this.parent.loc_return ?? 41;

          // Default business hours
          const minMinutes = currentDesk === 46 ? 555 : 510; // 555 = 9*60 + 15
          let maxMinutes = 1110; // 18:30
          // Example — if dropOffDesk 46 or 42 had different drop-off rules, adjust here if needed

          const hours = parseInt(value.split(":")[0], 10);
          const minutes = parseInt(value.split(":")[1], 10);
          const totalMinutes = hours * 60 + minutes;

          if (currentDesk === 46) {
            if (hours < 9 || (hours === 9 && minutes < 15)) {
              setNorthDeskOutOfHours(true);
            } else {
              setNorthDeskOutOfHours(false);
            }
          } else {
            setNorthDeskOutOfHours(false);
          }

          if (totalMinutes < minMinutes || totalMinutes > 1110) {
            // Return different messages based on current desk
            return this.createError({
              message:
                currentDesk === 46
                  ? t("northOutsideBusinessHours")
                  : t("outsideBusinessHours"),
            });
          }
          return totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
        }),
    });
  });

  const resolver = useMemo(
    () =>
      yupResolver(reservationTimeValidation, {
        context: {
          pickUpDesk,
          dropOffDesk,
        },
      }),
    [reservationTimeValidation],
  );

  const {
    formState: { errors, isValid },
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {
      loc_pickup: "41",
      date_pickup: dayjs().add(3, "day").format("YYYY-MM-DD"),
      time_pickup: dayjs(dayjs().hour(10).minute(0).second(0).toDate()).format(
        "HH:mm",
      ),
      loc_return: "41",
      date_return: dayjs().add(6, "day").format("YYYY-MM-DD"),
      time_return: dayjs(dayjs().hour(10).minute(0).second(0).toDate()).format(
        "HH:mm",
      ),
    },
    resolver: resolver,
    reValidateMode: "onChange",
  });

  const navigate = useNavigate();
  const handleChangePickUpDesk = useCallback(
    (event) => {
      setValue("loc_pickup", event.target.value, { shouldValidate: true });
      setValue("loc_return", event.target.value, { shouldValidate: true });
      setPickUpDesk(event.target.value ?? getValues("loc_pickup"));
      setDropOffDesk(event.target.value ?? getValues("loc_return"));

      if (
        event.target.value === 47 ||
        event.target.value === 46 ||
        event.target.value === 42
      ) {
        setOpenInformationModal(true);
      }

      // immediately trigger validation for pickup time with new context
      trigger(undefined, {
        context: {
          pickUpDesk: event.target.value,
          dropOffDesk: event.target.value,
        },
      });
    },
    [setValue, setPickUpDesk, setDropOffDesk, getValues],
  );

  const handleChangeDropOffDesk = useCallback(
    (event) => {
      setValue("loc_return", event.target.value, { shouldValidate: true });
      setDropOffDesk(event.target.value || getValues("loc_return"));
      if (
        event.target.value === 47 ||
        event.target.value === 46 ||
        event.target.value === 42
      ) {
        setOpenInformationModal(true);
      }
    },
    [setValue, setDropOffDesk, getValues],
  );

  const handleChangeTimePickup = useCallback(
    (time) => {
      if (time?.$m === 0) {
        setValue("time_pickup", `${time?.$H}:${time?.$m}0`, {
          shouldValidate: true,
        });
      } else {
        setValue("time_pickup", `${time?.$H}:${time?.$m}`, {
          shouldValidate: true,
        });
      }
    },
    [setValue],
  );

  const handleChangeTimeReturn = useCallback(
    (time) => {
      if (time?.$m === 0) {
        setValue("time_return", `${time?.$H}:${time?.$m}0`, {
          shouldValidate: true,
        });
      } else {
        setValue("time_return", `${time?.$H}:${time?.$m}`, {
          shouldValidate: true,
        });
      }
    },
    [setValue],
  );

  const handleChangeDatePickup = useCallback(
    (date) => {
      setValue("date_pickup", dayjs(date?.$d).format("YYYY-MM-DD"), {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const handleChangeDateReturn = useCallback(
    (date) => {
      setValue("date_return", dayjs(date?.$d).format("YYYY-MM-DD"), {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const [error, setError] = React.useState(null);

  const locations = useMemo(
    () => [
      { id: 41, name: "Funchal" },
      { id: 42, name: "Airport" },
    ],
    [],
  );
  const fetchAvailabilities = async () => {
    const newAvaiabilities = [];

    for (const location of locations) {
      setValue("loc_pickup", location.id);
      setValue("loc_return", location.id);

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: JSON.stringify(getValues()),
      };

      try {
        const response = await fetch(
          "https://www.whynotcarrental.com/Backend/vehicleAvaibility.php",
          requestOptions,
        );

        if (!response.ok) {
          // Log if the response is not OK (e.g., status 500)
          continue; // Skip this iteration
        }

        const vehicleAvaibility = await response.json();

        newAvaiabilities.push(
          vehicleAvaibility?.VehAvailRSCore?.VehVendorAvails?.VehVendorAvail
            ?.VehAvails,
        );
      } catch (error) {
        // Log the error and continue with the next iteration
      }
    }

    const mergedAvaiabilities = [
      ...new Set([...getAvaiabilities, ...newAvaiabilities]),
    ];

    setAvaiabilities(mergedAvaiabilities);
    return mergedAvaiabilities; // Return the mergedAvaiabilities
  };

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // ✅ Cleanup
  }, [window.innerWidth]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isTriggeredValid = await trigger(undefined, {
      context: {
        pickUpDesk,
        dropOffDesk,
      },
    });

    if (isTriggeredValid) {
      try {
        setIsloading(true);
        const avaiabilities = await fetchAvailabilities(); // Wait for fetchAvailabilities to complete

        setIsloading(false);
        navigate("/reservationCheckout", {
          state: {
            avaiabilities,
            reservationDetails: getValues(),
            pickUpDesk,
            dropOffDesk,
            width,
            choosedCar,
          },
        });
      } catch (error) {
        setIsloading(false);
        setError("Network error. Please try again later.");
      }
    } else {
      setOpenErrorModal(true);
      Object.entries(errors).forEach(([field, error]) => {});
    }
  };
  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
  };

  const handleCloseInformationModal = () => {
    setOpenInformationModal(false);
  };
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form onSubmit={(event) => handleSubmit(event)}>
          <Suspense fallback={null}>
            <ErrorModal
              open={openErrorModal}
              handleClose={handleCloseErrorModal}
              errors={errors}
            />
          </Suspense>
          <Suspense fallback={null}>
            <InformationModal
              open={openInformationModal}
              handleClose={handleCloseInformationModal}
              message={
                pickUpDesk === 47 ||
                pickUpDesk === 46 ||
                dropOffDesk === 47 ||
                dropOffDesk === 46
                  ? t("hotelDeliveryInfo")
                  : pickUpDesk === 42 || dropOffDesk === 42
                    ? t("airportDeliveryInfo")
                    : null
              }
            />
          </Suspense>
          {width < 800 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row", // Imagem e texto lado a lado
                alignItems: "center",
                justifyContent: "flex-start",
                borderRadius: 2,
                padding: 1.5,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Box
                component="img"
                src={carImage}
                alt="Oferta Carro"
                sx={{
                  width: "80px", // Imagem mais pequena para caber na barra
                  height: "auto",
                  objectFit: "contain",
                  marginRight: 2, // Espaçamento entre imagem e texto
                  filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "0.85rem",
                  lineHeight: 1.3,
                  textAlign: "left",
                }}
              >
                <strong>Oferta Especial:</strong> Condutor adicional{" "}
                <strong style={{ color: "#d32f2f" }}>GRÁTIS</strong> em todas as
                reservas!
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: width > 799 ? "flex-end" : "center", // Align the inner Box to the right
              alignItems: "center",
              height:
                width > 799 ? windowHeight - 100 : width < 400 ? "800px" : null, // Ensure the background covers the viewport height
              backgroundImage: `
      image-set(
        url(${background}) type("image/webp"),
        url("/Assets/background.png") type("image/png")
      )
    `,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center", // Center the background image
            }}
          >
            {width > 799 ? (
              <Stack
                direction="column"
                spacing={4} // Espaço entre o texto e a caixa de oferta
                sx={{
                  padding: 3,
                  ml: 10,
                  mt: 5,
                  maxWidth: "45%", // Permite que o texto ocupe um pouco mais de espaço
                }}
              >
                {/* Texto de Boas-vindas */}
                <Box sx={{ padding: 3, mb: 20, mr: 20, textAlign: "center" }}>
                  <Typography color="white" variant="h3" component="div">
                    Aventura-te!
                  </Typography>
                  <Typography color="white" variant="h5" component="div">
                    <span style={{ color: theme.palette.secondary.main }}>
                      Why Not
                    </span>{" "}
                    {t("title")}
                  </Typography>
                  <Typography
                    sx={{ mt: 2 }}
                    color="white"
                    variant="h5"
                    component="div"
                  >
                    {t("title2")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "rgba(249, 250, 252, 0.9)", // Glassmorphism style
                    padding: 2,
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderLeft: `5px solid ${theme.palette.secondary.main}`,
                    maxWidth: "fit-content", // A caixa ajusta-se ao tamanho do texto
                  }}
                >
                  {/* AQUI ESTÁ A MUDANÇA: USAR A IMAGEM PNG */}
                  <Box
                    component="img"
                    src={carImage} // A variável importada no topo
                    alt="Oferta Carro"
                    sx={{
                      width: "200px", // Ajusta este valor conforme o tamanho do teu PNG
                      height: "auto",
                      objectFit: "contain",
                      filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.2))", // Sombra bonita no PNG
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "0.9rem",
                      textAlign: "left",
                    }}
                  >
                    <strong>Oferta Especial:</strong> Condutor adicional{" "}
                    <strong style={{ color: "#d32f2f" }}>GRÁTIS</strong> em
                    todas as reservas!
                  </Typography>
                </Box>
              </Stack>
            ) : null}

            {/* LADO DIREITO (Apenas Formulário) */}
            <Box
              sx={{
                mr: { xs: 0, lg: 10 },
                width: { xs: "90%", sm: "80%", md: "60%", lg: "40%" }, // Ajuste de largura
                maxWidth: "500px",
                backgroundColor: "rgba(249, 250, 252, 0.8)",
                padding: 3,
                borderRadius: 4,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                mt: width > 400 && width < 799 ? 2 : 0,
                mb: width > 400 && width < 799 ? 2 : 0,
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingTop: "50px",
                    paddingBottom: "50px",
                  }}
                >
                  <CircularProgress size={100} thickness={2} />
                </Box>
              ) : (
                <>
                  <ScrollFab />
                  {width > 400 ? (
                    // === VERSÃO DESKTOP DO FORMULÁRIO (CÓDIGO ORIGINAL MANTIDO) ===
                    <>
                      <Grid container direction="column" width={"100%"}>
                        {/* Pick-up Section */}
                        <>
                          <Stack alignItems="center" direction="row">
                            <DirectionsCarIcon
                              sx={{
                                color: theme.palette.secondary.main,
                                fontSize: 30,
                              }}
                            />
                            <Typography
                              color={theme.palette.primary.main}
                              variant="h6"
                              fontWeight="bold"
                            >
                              {t("pickUp")}
                            </Typography>
                          </Stack>

                          <Grid container direction="column" sx={{ mb: 1 }}>
                            {/* Pick-Up Location */}
                            <Grid item>
                              <Typography
                                color={theme.palette.primary.main}
                                variant="subtitle1"
                                gutterBottom
                                id="loc_pickup"
                              >
                                {t("location")}
                              </Typography>
                              <FormControl fullWidth>
                                <Select
                                  size="small"
                                  labelid="loc_pickup"
                                  id="loc_pickup"
                                  value={getValues("loc_pickup")}
                                  name="loc_pickup"
                                  onChange={handleChangePickUpDesk}
                                  sx={{
                                    borderRadius: 2,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      "& legend": { display: "none" },
                                    },
                                  }}
                                >
                                  {DESKS.map(({ code, name }, index) => (
                                    <MenuItem key={index} value={code}>
                                      {name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid
                              container
                              direction="row"
                              spacing={2}
                              columns={2}
                            >
                              {/* Pick-Up Date */}
                              <Grid item sx={{ width: "50%" }} size={6}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("date")}
                                </Typography>
                                <FormControl fullWidth>
                                  <DatePicker
                                    size="small"
                                    disablePast
                                    name="date_pickup"
                                    format="DD-MM-YYYY"
                                    minDate={dayjs().add(2, "day")}
                                    value={dayjs(getValues("date_pickup"))}
                                    onChange={handleChangeDatePickup}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        sx: { borderRadius: 2 },
                                      },
                                    }}
                                  />
                                </FormControl>
                              </Grid>

                              {/* Pick-Up Time */}
                              <Grid item sx={{ width: "50%" }} size={6}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("time")}
                                </Typography>
                                <FormControl fullWidth>
                                  <TimePicker
                                    size="small"
                                    value={dayjs(
                                      getValues("time_pickup"),
                                      "HH:mm",
                                    )}
                                    minTime={
                                      pickUpDesk === 46
                                        ? dayjs("09:15", "HH:mm")
                                        : dayjs("08:30", "HH:mm")
                                    }
                                    maxTime={dayjs("18:30", "HH:mm")}
                                    onChange={handleChangeTimePickup}
                                    ampm={false}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        sx: { borderRadius: 2 },
                                      },
                                    }}
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                        </>

                        {/* Drop-off Section */}
                        <>
                          <Stack
                            alignItems="center"
                            direction="row"
                            sx={{ mt: 1 }}
                          >
                            <LocationOnIcon
                              sx={{
                                color: theme.palette.secondary.main,
                                fontSize: 30,
                              }}
                            />
                            <Typography
                              color={theme.palette.primary.main}
                              variant="h6"
                              fontWeight="bold"
                            >
                              {t("dropOff")}
                            </Typography>
                          </Stack>

                          <Grid container direction="column" sx={{ mb: 1 }}>
                            {/* Drop-Off Location */}
                            <Grid item>
                              <Typography
                                color={theme.palette.primary.main}
                                variant="subtitle1"
                                gutterBottom
                                id="loc_return"
                              >
                                {t("location")}
                              </Typography>
                              <FormControl fullWidth>
                                <Select
                                  size="small"
                                  labelid="loc_return"
                                  id="loc_return"
                                  value={getValues("loc_return")}
                                  name="loc_return"
                                  onChange={handleChangeDropOffDesk}
                                  sx={{
                                    borderRadius: 2,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      "& legend": { display: "none" },
                                    },
                                  }}
                                >
                                  {DESKS.map(({ code, name }, index) => (
                                    <MenuItem key={index} value={code}>
                                      {name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid
                              container
                              direction="row"
                              spacing={2}
                              columns={2}
                            >
                              {/* Drop-Off Date */}
                              <Grid item sx={{ width: "50%" }} size={6}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("date")}
                                </Typography>
                                <FormControl fullWidth>
                                  <DatePicker
                                    size="small"
                                    disablePast
                                    name="date_return"
                                    format="DD-MM-YYYY"
                                    minDate={dayjs(getValues("date_pickup"))}
                                    value={dayjs(getValues("date_return"))}
                                    onChange={handleChangeDateReturn}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        sx: { borderRadius: 2 },
                                      },
                                    }}
                                  />
                                </FormControl>
                              </Grid>

                              {/* Drop-Off Time */}
                              <Grid item sx={{ width: "50%" }} size={6}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("time")}
                                </Typography>
                                <FormControl fullWidth>
                                  <TimePicker
                                    size="small"
                                    value={dayjs(
                                      getValues("time_return"),
                                      "HH:mm",
                                    )}
                                    minTime={
                                      dropOffDesk === 46
                                        ? dayjs("09:15", "HH:mm")
                                        : dayjs("08:30", "HH:mm")
                                    }
                                    maxTime={dayjs("18:30", "HH:mm")}
                                    onChange={handleChangeTimeReturn}
                                    ampm={false}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        sx: { borderRadius: 2 },
                                      },
                                    }}
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                        </>
                      </Grid>
                    </>
                  ) : (
                    // === VERSÃO MOBILE (Mantém a lógica original para width < 400) ===
                    <>
                      <Grid container direction="column">
                        {/* Pick-up Label */}
                        <Stack
                          alignItems="center"
                          direction="row"
                          gap={1}
                          sx={{ marginBottom: 1 }}
                        >
                          <DirectionsCarIcon
                            sx={{
                              color: theme.palette.secondary.main,
                              fontSize: 30,
                            }}
                          />
                          <Typography
                            color={theme.palette.primary.main}
                            variant="h6" // Slightly smaller than h5 to match the image
                            fontWeight="bold"
                          >
                            {t("pickUp")}
                          </Typography>
                        </Stack>

                        {/* Pick-up Fields */}
                        <Grid
                          container
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flexWrap: "nowrap", // Default behavior
                            "@media (max-width: 610px)": {
                              flexWrap: "wrap", // Stack items vertically on small screens
                            },
                          }}
                        >
                          <Grid item>
                            <FormControl
                              aria-label="loc_pickup"
                              sx={{
                                minWidth: 250,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                labelid="loc_pickup"
                              >
                                {t("location")}
                              </Typography>
                              <Select
                                aria-label="loc_pickup"
                                labelid="loc_pickup"
                                id="loc_pickup"
                                value={getValues("loc_pickup")}
                                name="loc_pickup"
                                label={t("deliveryDesk")}
                                onError={(newError) => setError(newError)}
                                onChange={handleChangePickUpDesk}
                              >
                                {DESKS.map(({ code, name }, index) => (
                                  <MenuItem
                                    aria-label={name}
                                    key={index}
                                    value={code}
                                  >
                                    {name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item>
                            <Typography variant="subtitle1" gutterBottom>
                              {t("date")}
                            </Typography>
                            <FormControl
                              aria-label="date_pickup"
                              size="medium"
                              sx={{
                                "@media (max-width: 610px)": {
                                  minWidth: 250,
                                },
                              }}
                            >
                              <DatePicker
                                disablePast
                                name="date_pickup"
                                format="DD-MM-YYYY"
                                minDate={dayjs().add(3, "day")}
                                value={dayjs(getValues("date_pickup"))}
                                onChange={handleChangeDatePickup}
                                error={!!errors.date_pickup}
                                onError={(newError) => setError(newError)}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item>
                            <Typography variant="subtitle1" gutterBottom>
                              {t("time")}
                            </Typography>
                            <FormControl
                              aria-label="time_pickup"
                              size="medium"
                              sx={{
                                "@media (max-width: 610px)": {
                                  minWidth: 250,
                                },
                              }}
                            >
                              <TimePicker
                                value={dayjs(getValues("time_pickup"), "HH:mm")}
                                minTime={
                                  pickUpDesk === 46
                                    ? dayjs("09:15", "HH:mm")
                                    : dayjs("08:30", "HH:mm")
                                }
                                maxTime={dayjs("18:30", "HH:mm")}
                                onChange={handleChangeTimePickup}
                                ampm={false}
                                error={!!errors.time_pickup}
                                onError={(newError) => setError(newError)}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Drop-off Section */}
                      <Grid container direction="column" sx={{ marginTop: 4 }}>
                        {/* Drop-off Label */}
                        <Stack
                          alignItems="center"
                          direction="row"
                          sx={{ marginBottom: 1 }}
                        >
                          <LocationOnIcon
                            sx={{
                              color: theme.palette.secondary.main,
                              fontSize: 30,
                            }}
                          />
                          <Typography
                            color={theme.palette.primary.main}
                            variant="h6" // Slightly smaller than h5 to match the image
                            fontWeight="bold"
                          >
                            {t("dropOff")}
                          </Typography>
                        </Stack>

                        {/* Drop-off Fields */}
                        <Grid
                          container
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flexWrap: "nowrap", // Default behavior
                            "@media (max-width: 610px)": {
                              flexWrap: "wrap", // Stack items vertically on small screens
                            },
                          }}
                        >
                          <Grid item>
                            <FormControl
                              aria-label="loc_return"
                              sx={{
                                minWidth: 250,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                id="loc_return"
                              >
                                {t("location")}
                              </Typography>

                              <Select
                                aria-label="loc_return"
                                labelid="loc_return"
                                id="loc_return"
                                value={getValues("loc_return")}
                                name="loc_return"
                                label={t("returnDesk")}
                                onChange={handleChangeDropOffDesk}
                              >
                                {DESKS.map(({ code, name }, index) => (
                                  <MenuItem
                                    aria-label={name}
                                    key={index}
                                    value={code}
                                  >
                                    {name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item>
                            <Typography variant="subtitle1" gutterBottom>
                              {t("date")}
                            </Typography>
                            <FormControl
                              aria-label="date_return"
                              size="medium"
                              sx={{
                                "@media (max-width: 610px)": {
                                  minWidth: 250,
                                },
                              }}
                            >
                              <DatePicker
                                disablePast
                                name="date_return"
                                format="DD-MM-YYYY"
                                onError={(newError) => setError(newError)}
                                minDate={dayjs(getValues("date_pickup"))}
                                value={dayjs(getValues("date_return"))}
                                onChange={handleChangeDateReturn}
                                error={!!errors.date_return}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item>
                            <Typography variant="subtitle1" gutterBottom>
                              {t("time")}
                            </Typography>
                            <FormControl
                              aria-label="time_return"
                              size="medium"
                              sx={{
                                "@media (max-width: 610px)": {
                                  minWidth: 250,
                                },
                              }}
                            >
                              <TimePicker
                                value={dayjs(getValues("time_return"), "HH:mm")}
                                minTime={
                                  dropOffDesk === 46
                                    ? dayjs("09:15", "HH:mm")
                                    : dayjs("08:30", "HH:mm")
                                }
                                maxTime={dayjs("18:30", "HH:mm")}
                                onChange={handleChangeTimeReturn}
                                ampm={false}
                                onError={(newError) => setError(newError)}
                                error={!!errors.time_return}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {choosedCar?.brand ? (
                    <>
                      <Typography
                        color={theme.palette.primary.main}
                        variant="h5"
                        gutterBottom
                      >
                        {t("groupPreference")}
                      </Typography>
                      <Card sx={{ display: "flex" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: {
                              xs: "column",
                              sm: "row",
                              md: "row",
                              lg: "row",
                            },
                            width: "100%",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: {
                                xs: "column",
                                sm: "row",
                                md: "row",
                                lg: "row",
                              },
                            }}
                          >
                            <CardMedia
                              component="img"
                              sx={{ width: 151 }}
                              loading="lazy"
                              rel="preload"
                              image={
                                process.env.PUBLIC_URL +
                                "img_car/detail/" +
                                choosedCar.picture
                              }
                              alt="Live from space album cover"
                            />
                            <CardContent sx={{ flex: "1 0 auto" }}>
                              <Typography component="div" variant="h5">
                                {choosedCar.brand}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                component="div"
                                sx={{ color: "text.secondary" }}
                              >
                                {t("group")}{" "}
                                {vehicleCodeChanger(choosedCar.code)}
                              </Typography>
                            </CardContent>
                          </Box>
                          <IconButton
                            aria-label="clear"
                            onClick={() => setChoosedCar(null)}
                          >
                            <ClearIcon sx={{ fontSize: "2.5rem" }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </>
                  ) : null}

                  {/* Row for Checkbox and Search Button */}
                  <Grid container justifyContent="space-between">
                    <Grid
                      item
                      sx={{
                        mt: 2,
                        textAlign: {
                          lg: "center",
                          xl: "center",
                          sm: "center",
                          xs: "center",
                        },
                        width: "100%",
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={<SendIcon />}
                        size="large"
                        disabled={isLoading}
                        fullWidth
                        sx={{ borderRadius: 2, fontWeight: "bold" }}
                      >
                        {t("search")}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Box>
        </form>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

Body.propTypes = {
  showNavBar: PropTypes.bool,
  setChoosedCar: PropTypes.any,
  choosedCar: PropTypes.any,
};

export default Body;
